import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import {
  ComponentWorkspaceRow,
  ITcProductComponentInformation,
} from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { deriveProductTotals, toWorkspaceRow } from "./product-details/component-payload";
import EditComponentModal from "./edit-component.modal";

interface ComponentWorkspaceProps {
  articleNo: string;
  productSupplementaryWeight: number;
  components: ITcProductComponentInformation[];
  isSaving?: boolean;
  // Posts the component (parent owns the productId + endpoint); resolves true on 200.
  onSaveComponent: (component: ITcProductComponentInformation) => Promise<boolean>;
  onDeleteComponent: (component: ITcProductComponentInformation, index: number) => void;
  onContinueToProduct: () => void;
  onExit: () => void;
}

const emptyComponent = (articleNo: string): ITcProductComponentInformation => ({
  tcAssociatedProductArticleNo: articleNo,
  tcProductComponentNo: "",
  tcProductComponentDetail: "",
  tcProductComponentNetShippingWeight: 0,
  tcProductComponentInfoFiberLength: "",
  tcProductComponentInfoYarnCount: "",
  tcProductComponentInfoFabricWeightAndConstruct: "",
  tcProductComponentInfoFabricWeightOfFinishedProduct: "",
  tcProductComponentInfoTechnicalSpecifications: "",
  tcProductComponentInfoAdditional: "",
  tcProductComponentCertifiedWeightInformation: [],
  tcProductComponentRawMaterialInformation: [],
});

/**
 * Component Creation Workspace — full-width view that replaces the product stepper while open.
 * Shows a live "Product Total Derived from Components" summary, the component table, and gates
 * "Continue to Product Information" until at least one component exists.
 */
const ComponentWorkspace: React.FC<ComponentWorkspaceProps> = ({
  articleNo,
  productSupplementaryWeight,
  components,
  isSaving = false,
  onSaveComponent,
  onDeleteComponent,
  onContinueToProduct,
  onExit,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number>(-1);

  const rows: ComponentWorkspaceRow[] = useMemo(
    () => (components || []).map((c) => toWorkspaceRow(c)),
    [components]
  );

  const totals = useMemo(
    () => deriveProductTotals(components || [], productSupplementaryWeight),
    [components, productSupplementaryWeight]
  );

  const handleAdd = () => {
    setEditIndex(-1);
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditIndex(index);
    setModalOpen(true);
  };

  const handleSave = async (component: ITcProductComponentInformation): Promise<boolean> => {
    const ok = await onSaveComponent(component);
    if (ok) setModalOpen(false);
    return ok;
  };

  const currentComponent =
    editIndex >= 0 && components[editIndex]
      ? components[editIndex]
      : emptyComponent(articleNo);

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Component Creation Workspace for Product Article Number - {articleNo}
        </Typography>
        <InnerButton sx={{ boxShadow: "none" }} onClick={onExit}>
          Exit
        </InnerButton>
      </Box>

      {/* Product Total Derived from Components (live sums) — vertical Weight Type / Weight table.
          At the workspace stage Net Shipping = Σ component net (supplementary is added later, on the
          product steps), matching the wireframe (Net 1000 = component sum, not the full product net). */}
      <Box mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Product Total Derived from Components
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 440 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2C4748" }}>
                <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Weight Type</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="right">
                  Weight (kg)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Net Shipping Wt</TableCell>
                <TableCell align="right">{totals.componentNetSum.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Certified Wt</TableCell>
                <TableCell align="right">{totals.productCertifiedWeight.toFixed(2)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Non-Certified Wt</TableCell>
                <TableCell align="right">{totals.productNonCertifiedWeight.toFixed(2)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <StyledButton onClick={handleAdd}>Add Components</StyledButton>
      </Box>

      {/* Component table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#2C4748" }}>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Component Number</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Component Detail</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Component Net Shipping Wt (kg)
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Component Certified Wt (kg)
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Component Non-Certified Wt (kg)
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No components added yet. Click "Add Components" to begin.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={`${row.tcProductComponentNo}-${index}`}>
                  <TableCell>{row.tcProductComponentNo}</TableCell>
                  <TableCell>{row.tcProductComponentDetail}</TableCell>
                  <TableCell>{row.componentNetShippingWeight.toFixed(2)}</TableCell>
                  <TableCell>{row.componentCertifiedWeight.toFixed(2)}</TableCell>
                  <TableCell>{row.componentNonCertifiedWeight.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleEdit(index)} color="default" size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => onDeleteComponent(row, index)}
                      size="small"
                      style={{ color: "rgb(244, 67, 54)" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <StyledButton
          onClick={onContinueToProduct}
          disabled={rows.length === 0}
          title={
            rows.length === 0
              ? "Add at least one component to continue"
              : "Continue to Product Information"
          }
        >
          Continue to Product Information
        </StyledButton>
      </Box>

      {modalOpen && (
        <EditComponentModal
          open={modalOpen}
          articleNo={articleNo}
          component={currentComponent}
          isEdit={editIndex >= 0}
          isSaving={isSaving}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default ComponentWorkspace;
